import React, { useEffect, useRef, useState } from "react";
import Styles from "../../styles/exercise/exercisereport.module.scss";
import Navbar from "../../components/Navbar";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toggle } from "../../redux";
import { coachRoutes } from "../../routers/CoachRouters";
import {
  addBiometricExercise,
  getClients,
  getExercise,
  getClientBlockData,
  updateClientReportExerciseByReportId,
  getClientReportPdfSend,
} from "../../services";
import Close_Button from "../../assets/auth/close.png";
import Suceess from "../../assets/auth/success.png";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const BiometricExerciseReport = () => {
  const blockRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSidebarOpen = useSelector((state) => state.navbar.button);
  const reduxExercises = useSelector((state) => state.exercise.exercises);
  const clients = useSelector((state) => state.client.clients);
  const bodyWeightRef = useRef();
  const muscleRef = useRef();
  const bodyFatRef = useRef();
  const notesRef = useRef();
  // validation errors for biometric inputs
  const [bodyErrors, setBodyErrors] = useState({
    weight: "",
    muscle: "",
    fat: "",
  });

  // Form state
  const [exercises, setExercises] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Track fetched data
  const [blockData, setBlockData] = useState(null);
  const [fieldsDisabled, setFieldsDisabled] = useState(false);

  // Fix: Local state for immediate client updates
  const [localClients, setLocalClients] = useState([]);
  const [abortController, setAbortController] = useState(null);

  const blockOptions = [1, 2, 3, 4];

  // Fix: Consistent API call function
  const fetchClients = async (searchTerm = "") => {
    // Cancel previous request if exists
    if (abortController) {
      abortController.abort();
    }

    const controller = new AbortController();
    setAbortController(controller);
    setIsSearching(true);

    try {
      // Call the service directly instead of dispatch to get immediate response
      const response = await getClients(false, 1, -1, searchTerm, "latest");

      // Update local state immediately with the response
      if (response && response.data) {
        setLocalClients(response.data);
      } else if (response && Array.isArray(response)) {
        setLocalClients(response);
      }

      // Also dispatch to Redux for consistency
      dispatch(getClients(false, 1, -1, searchTerm, "latest"));
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching clients (ExerciseReport):", error);
      }
    } finally {
      setIsSearching(false);
      setAbortController(null);
    }
  };
  const handleWheelPrevent = (e) => {
    e.target.blur();
    e.preventDefault();
  };
  useEffect(() => {
    if (Array.isArray(reduxExercises)) {
      setExercises(
        reduxExercises
          .filter((ex) => {
            return (
              ex &&
              typeof ex === "object" &&
              ex._id &&
              ex.name &&
              typeof ex.name === "string" &&
              ex.name.trim() !== "" &&
              ex.unit &&
              typeof ex.unit === "string" &&
              ex.unit.trim() !== "" &&
              (ex.unit === "CALS" || ex.unit === "KG" || ex.unit === "OTHER")
            );
          })
          .map((ex, idx) => ({
            id: ex._id || idx,
            exerciseId: ex._id,
            name: ex.name.trim(),
            unit: ex.unit.trim(),
            exerciseData: ex,
            timeMinutes: ex.duration?.default || 10,
            reps: ex.reps?.default || 5,
            scoreAchieved: "",
            total: "",
          }))
      );
    }
  }, [reduxExercises]);

  // Initial fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        await getExercise();
        // Fetch clients using our consistent function
        fetchClients();
      } catch (error) {
        console.error("Error fetching exercises:", error);
      }
    };
    fetchData();
  }, []);

  // Fix: Updated filtering logic with both local and Redux clients
  useEffect(() => {
    // Use localClients if available, otherwise fall back to Redux clients
    const clientsToFilter =
      localClients.length > 0 ? localClients : clients || [];

    if (clientsToFilter && clientSearch) {
      const filtered = clientsToFilter.filter((client) => {
        const name = (client.name || "").toLowerCase();
        const code = (client.code || "").toLowerCase();
        const email = (client.email || client.emailAddress || "").toLowerCase();

        const searchLower = clientSearch.toLowerCase();
        return (
          name.includes(searchLower) ||
          code.includes(searchLower) ||
          email.includes(searchLower)
        );
      });
      setFilteredClients(filtered);
    } else {
      setFilteredClients(clientsToFilter);
    }
  }, [clientSearch, clients, localClients]);

  // Fetch block data when client & block selected
  useEffect(() => {
    if (selectedClient && selectedBlock) fetchClientBlockData();
  }, [selectedClient, selectedBlock]);

  const fetchClientBlockData = async () => {
    setIsLoadingData(true);
    try {
      const data = await getClientBlockData(selectedClient._id, selectedBlock);
      setBlockData(data.report || null);
      setBlockData((prev) => ({
        ...prev,
        _topBlocks: data.blocks, // attach blocks for navigation check
      }));

      const hasWorkouts =
        Array.isArray(data?.report.workouts) && data.report.workouts.length > 0;
      const allWorkoutCompleted =
        data?.report.allWorkoutEntryCompleted || false;

      setFieldsDisabled(false);
      if (bodyWeightRef.current) {
        bodyWeightRef.current.value =
          data?.report.body?.weight !== undefined
            ? data.report.body.weight
            : "";
      }
      if (muscleRef.current) {
        muscleRef.current.value =
          data?.report.body?.muscle !== undefined
            ? data.report.body.muscle
            : "";
      }
      if (bodyFatRef.current) {
        bodyFatRef.current.value =
          data?.report.body?.fat !== undefined ? data.report.body.fat : "";
      }
      if (notesRef.current) {
        // support both `notes` and `note`
        notesRef.current.value =
          (data?.report.notes ?? data?.report.note) || "";
      }

      setExercises((prev) =>
        prev.map((row) => {
          if (hasWorkouts) {
            const wk = data.report.workouts.find(
              (w) => w.workout?._id === row.exerciseId
            );
            if (wk) {
              return {
                ...row,
                timeMinutes: wk.duration ?? row.timeMinutes,
                reps: wk.reps ?? row.reps,
                scoreAchieved: wk.scoreAchieved?.toString() ?? "",
                total: wk.unifiedMetricScore?.toString() ?? "",
              };
            }
          }
          return { ...row, scoreAchieved: "", total: "" };
        })
      );
    } catch (err) {
      console.error("Error fetching client block data:", err);
    } finally {
      setIsLoadingData(false);
    }
  };

  // Handle row changes
  const handleRowChange = (id, field, value) => {
    setExercises((prev) =>
      prev.map((row) => {
        if (row.id === id) {
          const updated = { ...row, [field]: value };
          if (field === "scoreAchieved") {
            const score = parseFloat(updated.scoreAchieved) || 0;
            const max = updated.exerciseData.score?.max || 100;
            updated.total =
              score > 0 ? Math.round((score / max) * 100).toString() : "";
          }
          return updated;
        }
        return row;
      })
    );
  };

  // Fix: Updated client search handler
  const handleClientSearch = (value) => {
    setClientSearch(value);
    setShowClientDropdown(true);
    setSelectedClient(null);

    if (value.trim()) {
      fetchClients(value.trim());
    } else {
      fetchClients(); // Fetch all clients when search is empty
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setClientSearch(`${client.code || "-"} - ${client.name || "-"}`);
    setShowClientDropdown(false);
  };

  const handleClientInputBlur = () => {
    setTimeout(() => setShowClientDropdown(false), 200);
  };

  // Fix: Updated focus handler
  const handleClientInputFocus = () => {
    setShowClientDropdown(true);
    // Only fetch if we don't have clients loaded
    if (
      !isSearching &&
      localClients.length === 0 &&
      (!clients || clients.length === 0)
    ) {
      fetchClients();
    }
  };

  const handleBlockChange = (v) => setSelectedBlock(v);
  const handleMainContainerClick = () => isSidebarOpen && dispatch(toggle());

  // Updated button logic based on allWorkoutEntryCompleted
  const workoutsExist = Boolean(blockData?.workouts?.length);
  const bodyExists = Boolean(blockData?.body);
  const allWorkoutCompleted = Boolean(blockData?.allWorkoutEntryCompleted);

  // Primary button text logic
  const getPrimaryButtonText = () => {
    if (!bodyExists) {
      return "Save";
    }
    if (!workoutsExist) {
      return "Save";
    }
    if (workoutsExist && !allWorkoutCompleted) {
      return "Save";
    }
    return "Save";
  };

  const primaryText = getPrimaryButtonText();
  const showSaveShare =
    !allWorkoutCompleted || (allWorkoutCompleted && !bodyExists);
  const showShare = allWorkoutCompleted && bodyExists;

  // Primary action
  const handlePrimary = () => {
    if (allWorkoutCompleted && !bodyExists) {
      navigate(coachRoutes.createBiometricReport);
    } else {
      handleSubmit();
    }
  };

  const areAllBlocksCompleted = (blocksObj) => {
    return (
      blocksObj &&
      blocksObj["1"] === true &&
      blocksObj["2"] === true &&
      blocksObj["3"] === true &&
      blocksObj["4"] === true
    );
  };

  const shouldNavigateAfterSave = (prevBlocks, newBlocks, currentBlock) => {
    // All blocks must be true in newBlocks
    const allTrue = ["1", "2", "3", "4"].every((b) => newBlocks?.[b] === true);
    // The current block must have changed from false to true
    const wasFalse = prevBlocks?.[currentBlock] === false;
    const isNowTrue = newBlocks?.[currentBlock] === true;
    return allTrue && wasFalse && isNowTrue;
  };

  const showSuccessModal = (
    completedCount,
    totalCount,
    hasBiometric = false,
    blocksObj = null,
    prevBlocksObj = null,
    currentBlock = null
  ) => {
    let message;
    if (completedCount === totalCount) {
      message = `Exercise Report Fully Completed - ${completedCount} Exercises Created`;
    } else {
      message = `Exercise Report Successfully Created - ${completedCount} Exercise${
        completedCount !== 1 ? "s" : ""
      } Completed`;
    }

    Swal.fire({
      html: `
      <div style="text-align: center; border-radius: 20px; position: relative;">
       <button 
          id="customCloseButton" 
          style="position: absolute; top:-10px; right:-20px; background: none; border: none; cursor: pointer;">
          <img 
          src=${Close_Button} 
            alt="Close" 
            style="width: 50px; height: 50px;"
          />
        </button>
        <img 
          src=${Suceess} 
          alt="Success" 
          style="width: 100px; margin-bottom: 20px;margin-top:30px;" 
        />
        <p style="color: #151515; text-align:center; font-family:gothambold; font-size:24px;">
         ${message}
        </p>
        <br/>
        <button 
          id="ok" 
          style="background: white; color: #151515;  padding: 15px 20px; font-family:gothambold; border-radius: 8px; cursor: pointer; margin-top: 20px;border: 1px solid #E27808;width:250px;height:48px;font-size:14px;">
          ok
        </button>
      </div>
    `,
      showConfirmButton: false,
      customClass: {
        popup: "custom-swal-container",
      },
      didOpen: () => {
        const ok = document.getElementById("ok");
        const closeButton = document.getElementById("customCloseButton");

        const handleClose = () => {
          // If all blocks are true, or if the current block was just completed, navigate
          if (
            areAllBlocksCompleted(blocksObj) ||
            shouldNavigateAfterSave(prevBlocksObj, blocksObj, currentBlock)
          ) {
            Swal.close();
            navigate(coachRoutes.listClient);
          } else {
            Swal.close();
          }
        };

        ok.addEventListener("click", handleClose);
        closeButton.addEventListener("click", handleClose);
      },
    });
  };
  const clearAllFields = (clearClientFields = false) => {
    // Reset exercise data to initial state
    setExercises((prev) =>
      prev.map((row) => ({
        ...row,
        timeMinutes: row.exerciseData.duration?.default || 10,
        reps: row.exerciseData.reps?.default || 5,
        scoreAchieved: "",
        total: "",
      }))
    );

    // Clear client selection only if requested
    if (clearClientFields) {
      setSelectedClient(null);
      setClientSearch("");
      setShowClientDropdown(false);
    }

    // Clear block selection
    setSelectedBlock("");

    // Clear biometric refs
    if (bodyWeightRef.current) bodyWeightRef.current.value = "";
    if (muscleRef.current) muscleRef.current.value = "";
    if (bodyFatRef.current) bodyFatRef.current.value = "";
    if (notesRef.current) notesRef.current.value = "";

    // Clear validation errors
    setBodyErrors({
      weight: "",
      muscle: "",
      fat: "",
    });

    // Reset block data and form state
    setBlockData(null);
    setFieldsDisabled(false);

    console.log("✅ All form fields cleared");
  };
  // Submit
  const handleSubmit = async () => {
    if (!selectedClient || !selectedBlock) return;

    // Prevent submit if any biometric validation error exists
    if (Object.values(bodyErrors).some((v) => v && v.length > 0)) {
      toast.error("Please fix biometric values before submitting.");
      return;
    }

    const completedExercises = exercises.filter(
      (r) => r.scoreAchieved && parseFloat(r.scoreAchieved) > 0
    );
    const completedCount = completedExercises.length;
    const totalCount = exercises.length;

    const allExercises = exercises.map((r) => {
      const isFilled = r.scoreAchieved || r.total;

      return {
        workout: r.exerciseId,
        predefinedScore: r.exerciseData.score,
        unit: r.exerciseData.unit,
        duration: r.unit === "CALS" ? parseInt(r.timeMinutes, 10) : undefined,
        reps: r.unit === "KG" ? parseInt(r.reps, 10) : undefined,
        scoreAchieved: isFilled ? parseFloat(r.scoreAchieved) || 0 : 0,
        unifiedMetricScore: isFilled ? parseFloat(r.total) || 0 : 0,
      };
    });

    // Helper: parse to number or undefined if invalid/empty
    const toNum = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : undefined;
    };

    // Check if ALL three biometric fields are filled
    const bodyWeight = toNum(bodyWeightRef.current?.value);
    const muscle = toNum(muscleRef.current?.value);
    const bodyFat = toNum(bodyFatRef.current?.value);
    const notes = notesRef.current?.value?.trim() || "";

    // Only send body data if ALL three fields are provided
    const hasAllBiometricData =
      bodyWeight !== undefined && muscle !== undefined && bodyFat !== undefined;

    const hasBiometricData = hasAllBiometricData || notes !== "";

    const formData = {
      client: selectedClient._id,
      block: parseInt(selectedBlock, 10),
      workouts: allExercises,
      body: hasAllBiometricData
        ? {
            weight: bodyWeight,
            muscle: muscle,
            fat: bodyFat,
          }
        : null,
      notes: notes,
    };

    // --- REDIRECT LOGIC BEFORE ADD ---
    const prevBlocks = blockData?._topBlocks;
    const currentBlockNum = String(selectedBlock);
    if (prevBlocks) {
      // All blocks except current must be true, current block must be false
      const otherBlocksTrue = ["1", "2", "3", "4"]
        .filter((b) => b !== currentBlockNum)
        .every((b) => prevBlocks?.[b] === true);
      const wasFalse = prevBlocks?.[currentBlockNum] === false;
      if (otherBlocksTrue && wasFalse) {
        // After submit, all blocks will be completed, but this prevents addBiometricExercise from being called!
        // Fix: Do NOT return here, just set a flag to redirect after add
        var shouldRedirectAfterAdd = true;
      }
    }
    // --- END REDIRECT LOGIC ---

    try {
      let responseData;
      if (
        blockData &&
        (blockData.workouts ||
          (blockData.body && blockData.workouts.length > 0))
      ) {
        responseData = await updateClientReportExerciseByReportId(
          blockData._id,
          formData
        );
        if (responseData) {
          showSuccessModal(
            completedCount,
            totalCount,
            hasBiometricData,
            responseData?.blocks || blockData?._topBlocks,
            prevBlocks,
            currentBlockNum
          );
        }
        clearAllFields(false); // Do not clear client fields
      } else {
        responseData = await addBiometricExercise(formData);
        if (shouldRedirectAfterAdd) {
          navigate(coachRoutes.listClient);
          clearAllFields(true); // Clear client fields on redirect
          return;
        }
        if (responseData) {
          showSuccessModal(
            completedCount,
            totalCount,
            hasBiometricData,
            responseData?.blocks,
            prevBlocks,
            currentBlockNum
          );
        }
        clearAllFields(false); // Do not clear client fields
      }
    } catch (err) {
      console.error(err);
    }
  };
  const handleSubmitShare = async () => {
    if (!selectedClient || !selectedBlock) return;

    // Prevent submit if any biometric validation error exists
    if (Object.values(bodyErrors).some((v) => v && v.length > 0)) {
      toast.error("Please fix biometric values before submitting.");
      return;
    }

    const completedExercises = exercises.filter(
      (r) => r.scoreAchieved && parseFloat(r.scoreAchieved) > 0
    );
    const completedCount = completedExercises.length;
    const totalCount = exercises.length;

    const allExercises = exercises.map((r) => {
      const isFilled = r.scoreAchieved || r.total;

      return {
        workout: r.exerciseId,
        predefinedScore: r.exerciseData.score,
        unit: r.exerciseData.unit,
        duration: r.unit === "CALS" ? parseInt(r.timeMinutes, 10) : undefined,
        reps: r.unit === "KG" ? parseInt(r.reps, 10) : undefined,
        scoreAchieved: isFilled ? parseFloat(r.scoreAchieved) || 0 : 0,
        unifiedMetricScore: isFilled ? parseFloat(r.total) || 0 : 0,
      };
    });

    // Helper: parse to number or undefined if invalid/empty
    const toNum = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : undefined;
    };

    // Check if ALL three biometric fields are filled
    const bodyWeight = toNum(bodyWeightRef.current?.value);
    const muscle = toNum(muscleRef.current?.value);
    const bodyFat = toNum(bodyFatRef.current?.value);
    const notes = notesRef.current?.value?.trim() || "";

    // Only send body data if ALL three fields are provided
    const hasAllBiometricData =
      bodyWeight !== undefined && muscle !== undefined && bodyFat !== undefined;

    const hasBiometricData = hasAllBiometricData || notes !== "";

    const formData = {
      client: selectedClient._id,
      block: parseInt(selectedBlock, 10),
      workouts: allExercises,
      body: hasAllBiometricData
        ? {
            weight: bodyWeight,
            muscle: muscle,
            fat: bodyFat,
          }
        : null,
      notes: notes,
    };

    try {
      let responseData;
      const prevBlocks = blockData?._topBlocks;
      const currentBlockNum = String(selectedBlock);

      if (
        blockData &&
        (blockData.workouts ||
          (blockData.body && blockData.workouts.length > 0))
      ) {
        responseData = await updateClientReportExerciseByReportId(
          blockData._id,
          formData
        );
        clearAllFields(false); // Do not clear client fields

        if (responseData) {
          const success = await getClientReportPdfSend(selectedClient._id);
          if (success) {
            showSuccessModal(
              completedCount,
              totalCount,
              hasBiometricData,
              responseData?.blocks || blockData?._topBlocks,
              prevBlocks,
              currentBlockNum
            );
          }
        }
      } else {
        responseData = await addBiometricExercise(formData);
        clearAllFields(false); // Do not clear client fields

        if (responseData) {
          const success = await getClientReportPdfSend(selectedClient._id);
          if (success) {
            showSuccessModal(
              completedCount,
              totalCount,
              hasBiometricData,
              responseData?.blocks,
              prevBlocks,
              currentBlockNum
            );
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveAndShare = handleSubmitShare;
  const handleSave = handleSubmit;
  const handleCancel = () => navigate(coachRoutes.listClient);

  // Fix: Improved email display helper
  const getEmailDisplay = (client) => {
    if (!client) return "No email";

    // Check multiple possible email field names
    const email =
      client.email || client.emailAddress || client.Email || client.mail;

    if (!email || email.trim() === "") {
      // Check if email field exists at all
      const hasEmailField =
        client.hasOwnProperty("email") ||
        client.hasOwnProperty("emailAddress") ||
        client.hasOwnProperty("Email") ||
        client.hasOwnProperty("mail");

      if (!hasEmailField) {
        console.log("Client missing email field entirely:", client);
        return "Email field missing";
      } else {
        console.log("Client with empty email:", client);
        return "No email";
      }
    }

    return email;
  };

  // Fix: Better input change handler
  const handleClientInputChange = (e) => {
    const value = e.target.value;
    handleClientSearch(value);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [abortController]);

  // Render fields helper
  const renderConditionalFields = (row) => {
    const { unit, exerciseData, timeMinutes, reps, scoreAchieved } = row;
    const getUnitDisplay = (u) =>
      u === "CALS" ? "Cals" : u === "KG" ? "kg" : u;

    if (unit === "CALS") {
      const options = exerciseData.duration?.values || [10, 20, 30, 40, 50];
      return (
        <>
          <div className={Styles.input_group}>
            <label className={Styles.label}>Duration (Minutes)</label>
            <select
              className={Styles.dropdown_select}
              value={timeMinutes}
              disabled={false}
              onChange={(e) =>
                handleRowChange(row.id, "timeMinutes", e.target.value)
              }
            >
              {options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt} minutes
                </option>
              ))}
            </select>
          </div>
          <div className={Styles.input_group}>
            <label className={Styles.label}>Score Achieved</label>
            <div className={Styles.input_with_unit}>
              <input
                type="number"
                className={Styles.input_with_suffix}
                placeholder="Enter score"
                disabled={false}
                value={scoreAchieved}
                max={exerciseData.score?.max || 100}
                min={exerciseData.score?.min || 0}
                onChange={(e) =>
                  handleRowChange(row.id, "scoreAchieved", e.target.value)
                }
              />
              <span className={Styles.unit_suffix}>{getUnitDisplay(unit)}</span>
            </div>
          </div>
        </>
      );
    } else if (unit === "KG") {
      const options = exerciseData.reps?.values || [5];
      return (
        <>
          <div className={Styles.input_group}>
            <label className={Styles.label}>Reps</label>
            <select
              className={Styles.dropdown_select}
              value={`${reps} RM`}
              disabled={false}
              onChange={(e) =>
                handleRowChange(
                  row.id,
                  "reps",
                  e.target.value.replace(" RM", "")
                )
              }
            >
              {options.map((opt) => (
                <option key={opt} value={`${opt} RM`}>
                  {opt} RM
                </option>
              ))}
            </select>
          </div>
          <div className={Styles.input_group}>
            <label className={Styles.label}>Score Achieved</label>
            <div className={Styles.input_with_unit}>
              <input
                type="number"
                className={Styles.input_with_suffix}
                placeholder="Enter score"
                disabled={false}
                value={scoreAchieved}
                max={exerciseData.score?.max || 100}
                min={exerciseData.score?.min || 0}
                onChange={(e) =>
                  handleRowChange(row.id, "scoreAchieved", e.target.value)
                }
                onWheel={handleWheelPrevent}
              />
              <span className={Styles.unit_suffix}>{getUnitDisplay(unit)}</span>
            </div>
          </div>
        </>
      );
    } else {
      return (
        <>
          <div className={Styles.input_group}>
            <label className={Styles.label}>Duration (Minutes)</label>
            <select className={Styles.dropdown_select} disabled>
              <option>10 minutes</option>
            </select>
          </div>
          <div className={Styles.input_group}>
            <label className={Styles.label}>Score Achieved</label>
            <input type="text" placeholder="Select exercise" disabled />
          </div>
        </>
      );
    }
  };

  // Validate biometric inputs on change / input
  const handleBiometricChange = (field, e) => {
    const val = e.target.value;
    const n = parseFloat(val);
    let err = "";

    if (val === "" || val === undefined) {
      err = "";
    } else if (isNaN(n)) {
      err = "Invalid value";
    } else {
      if ((field === "weight" || field === "muscle") && n > 200) {
        err = "Maximum value is 200";
      }
      if (field === "fat" && n > 100) {
        err = "Maximum value is 100";
      }
      if (n < 0) {
        err = "Value cannot be negative";
      }
    }

    // set HTML5 custom validity for native tooltip as well
    try {
      if (err) e.target.setCustomValidity(err);
      else e.target.setCustomValidity("");
    } catch (e) {
      // ignore if setCustomValidity not supported
    }

    setBodyErrors((prev) => ({ ...prev, [field]: err }));
  };

  return (
    <>
      <Navbar />
      <div
        className={Styles.createsession_main}
        onClick={handleMainContainerClick}
      >
        <div className={Styles.createsession_container}>
          <h2>
            <Link to={coachRoutes.dashboard}>HOME / </Link>
            <span className={Styles.orange}>
              Create Biometric And Exercise{" "}
            </span>
          </h2>
          <div className={Styles.form_container}>
            <div className={Styles.table_container_headingone}>
              <h3>CREATE BIOMETRIC AND EXERCISE REPORT</h3>
            </div>
            <div className={Styles.row_padding}>
              {/* Client & Block */}
              <div className={Styles.row}>
                <div
                  className={Styles.input_group}
                  style={{ position: "relative" }}
                >
                  <label className={Styles.label}>ID/Name</label>
                  <input
                    type="text"
                    placeholder="Search client"
                    value={clientSearch}
                    onChange={handleClientInputChange}
                    onFocus={handleClientInputFocus}
                    onBlur={handleClientInputBlur}
                    required
                  />
                  {isSearching && (
                    <div className={Styles.search_loading}>
                      <span>Searching...</span>
                    </div>
                  )}
                  {showClientDropdown &&
                    !isSearching &&
                    !allWorkoutCompleted && (
                      <div
                        className={Styles.client_dropdown}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {filteredClients.length ? (
                          filteredClients.map((c) => (
                            <div
                              key={c._id}
                              className={Styles.client_option}
                              onClick={() => handleClientSelect(c)}
                            >
                              <div className={Styles.client_name}>
                                {c.code} - {c.name}
                              </div>
                              <div className={Styles.client_details}>
                                {getEmailDisplay(c)}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className={Styles.no_results}>
                            No clients found
                          </div>
                        )}
                      </div>
                    )}
                </div>
                <div className={Styles.input_group}>
                  <label className={Styles.label}>Block</label>
                  <select
                    ref={blockRef}
                    className={Styles.dropdown_select}
                    value={selectedBlock}
                    onChange={(e) => handleBlockChange(e.target.value)}
                  >
                    <option value="" disabled>
                      Choose block
                    </option>
                    {blockOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        Block {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {isLoadingData && (
                <div className={Styles.loading_indicator}>
                  <p>Loading data...</p>
                </div>
              )}
              {/* ── Client + Block Row ── */}

              {/* ── Biometric Inputs ── */}
              <div className={Styles.row}>
                <div className={Styles.input_group}>
                  <label className={Styles.label}>Body Weight (kg)</label>
                  <input
                    ref={bodyWeightRef}
                    type="number"
                    placeholder="Enter here"
                    disabled={false}
                    max={200}
                    min={0}
                    onChange={(e) => handleBiometricChange("weight", e)}
                    onWheel={handleWheelPrevent}
                  />
                  {bodyErrors.weight && (
                    <div
                      style={{ color: "#D32F2F", fontSize: 12, marginTop: 4 }}
                    >
                      {bodyErrors.weight}
                    </div>
                  )}
                </div>
                <div className={Styles.input_group}>
                  <label className={Styles.label}>Muscle (kg)</label>
                  <input
                    ref={muscleRef}
                    type="number"
                    placeholder="Enter here"
                    disabled={false}
                    max={200}
                    min={0}
                    onChange={(e) => handleBiometricChange("muscle", e)}
                    onWheel={handleWheelPrevent}
                  />
                  {bodyErrors.muscle && (
                    <div
                      style={{ color: "#D32F2F", fontSize: 12, marginTop: 4 }}
                    >
                      {bodyErrors.muscle}
                    </div>
                  )}
                </div>
                <div className={Styles.input_group}>
                  <label className={Styles.label}>Body Fat (%)</label>
                  <input
                    ref={bodyFatRef}
                    type="number"
                    step="0.1"
                    placeholder="Enter here"
                    disabled={false}
                    max={100}
                    min={0}
                    onChange={(e) => handleBiometricChange("fat", e)}
                    onWheel={handleWheelPrevent}
                  />
                  {bodyErrors.fat && (
                    <div
                      style={{ color: "#D32F2F", fontSize: 12, marginTop: 4 }}
                    >
                      {bodyErrors.fat}
                    </div>
                  )}
                </div>
              </div>

              <div className={Styles.notes_section}>
                <div className={Styles.input_group}>
                  <label className={Styles.label}>Notes</label>
                  <textarea
                    ref={notesRef}
                    className={Styles.notes_textarea}
                    placeholder="Enter additional notes here..."
                    rows="4"
                    disabled={false}
                  />
                </div>
              </div>
              {/* Exercise rows */}
              {exercises.map((row) => (
                <div key={row.id} className={Styles.row}>
                  <div className={Styles.input_group}>
                    <label className={Styles.label}>Exercise</label>
                    <input
                      type="text"
                      value={row.name}
                      readOnly
                      disabled
                      className={Styles.readonly_input}
                    />
                  </div>
                  {renderConditionalFields(row)}
                  <div className={Styles.input_group}>
                    <label className={Styles.label}>Total</label>
                    <input type="text" readOnly value={`${row.total}%`} />
                  </div>
                </div>
              ))}
              {/* Checkboxes */}
              {/* <div className={Styles.input_radio}>
                <label>
                  <input
                    type="checkbox"
                    name="biometrics"
                    checked={bodyExists}
                    disabled={true}
                  />
                  <span></span>
                  Biometrics
                </label>
                <label>
                  <input
                    name="exercise"
                    type="checkbox"
                    checked={allWorkoutCompleted}
                    disabled={true}
                  />
                  <span></span>
                  Exercise
                </label>
              </div> */}
            </div>

            <div className={Styles.button_group_line}></div>
            {/* Buttons */}
            <div className={Styles.button_group}>
              {/* <button className={Styles.save_button} onClick={handlePrimary}>
                {primaryText}
              </button> */}
              <button className={Styles.save_button} onClick={handleSave}>
                Save
              </button>
              <button
                className={Styles.save_share_button}
                onClick={handleSaveAndShare}
              >
                Save & Share
              </button>

              <button className={Styles.cancel_button} onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BiometricExerciseReport;
